import { useState, useEffect, useCallback } from 'react';
import { Plus, BarChart3, Target, Zap, Clock, FileText, Search, Globe, Settings, PenLine, Trash2, Loader2, Sparkles, BookOpen, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

// Sub-components
import { API, STATUTS, MOCK_DATA } from './crm/constants';
import { GoProspecter } from './crm/GoProspecter';
import { CrmDashboard } from './crm/CrmDashboard';
import { CrmPipeline } from './crm/CrmPipeline';
import { CrmProgrammes } from './crm/CrmProgrammes';
import { CrmRecherche } from './crm/CrmRecherche';
import { CrmSources } from './crm/CrmSources';
import { CrmConfig } from './crm/CrmConfig';
import { ProspectDetail } from './crm/ProspectDetail';
import { CreateProspectModal } from './crm/CreateProspectModal';
import { CrmRapide } from './crm/CrmRapide';
import { CrmRelances } from './crm/CrmRelances';
import { CrmSignatures } from './crm/CrmSignatures';
import { CrmCorbeille } from './crm/CrmCorbeille';
import { CrmIAKnowledge } from './crm/CrmIAKnowledge';
import { CrmEmailLogs } from './crm/CrmEmailLogs';
import { CrmAIAssistant } from './crm/CrmAIAssistant';
import { CrmGuide } from './crm/CrmGuide';

export default function CrmPage({ initialTab = 'dashboard' }) {
  const [tab, setTab] = useState(initialTab);
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({ total: 0, pipeline: {}, communications: {}, enrichis: 0, ca_realise: 0, ca_potentiel: 0, activity: [] });
  const [loading, setLoading] = useState(true);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showGoProspecter, setShowGoProspecter] = useState(false);
  const [hotProspects, setHotProspects] = useState([]);
  const [objectif, setObjectif] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [caSecteurs, setCaSecteurs] = useState({});
  const [dismissedHot, setDismissedHot] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const [pRes, sRes, oRes, catRes, csRes] = await Promise.all([
        fetch(`${API}/api/crm/prospects`), fetch(`${API}/api/crm/stats`),
        fetch(`${API}/api/crm/objectif`), fetch(`${API}/api/crm/catalogue`),
        fetch(`${API}/api/crm/stats/ca-secteurs`),
      ]);
      const [pData, sData, oData, catData, csData] = await Promise.all([pRes.json(), sRes.json(), oRes.json(), catRes.json(), csRes.json()]);
      setProspects(pData); setStats(sData); setObjectif(oData); setCatalogue(catData); setCaSecteurs(csData);
      const hot = pData.filter(p => (p.scoreIA >= 65 || p.statut === 'devis') && !['gagne', 'perdu'].includes(p.statut) && !dismissedHot.has(p.id))
        .sort((a, b) => (b.scoreIA || 0) - (a.scoreIA || 0)).slice(0, 8);
      setHotProspects(hot);
    } catch { toast.error('Erreur chargement CRM'); }
    setLoading(false);
  }, [dismissedHot]);

  useEffect(() => { load(); }, [load]);

  const relances = prospects.filter(p => {
    const today = new Date().toISOString().split('T')[0];
    return p.relanceDate && p.relanceDate <= today && !['gagne', 'perdu'].includes(p.statut);
  });

  const handleSetObjectif = async (val) => {
    try {
      await fetch(`${API}/api/crm/objectif`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ objectif_mensuel: val }) });
      setObjectif(prev => ({ ...prev, objectif_mensuel: val }));
      toast.success('Objectif mis a jour');
    } catch { toast.error('Erreur'); }
  };

  const deleteProspect = async (id) => {
    try { await fetch(`${API}/api/crm/prospects/${id}`, { method: 'DELETE' }); toast.success('Mis a la corbeille'); load(); } catch { toast.error('Erreur'); }
  };

  const bulkUpdate = async (ids, updates) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API}/api/crm/prospects/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) })));
      toast.success(`${ids.length} prospect(s) mis a jour`); load();
    } catch { toast.error('Erreur'); }
  };

  const bulkDelete = async (ids) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API}/api/crm/prospects/${id}`, { method: 'DELETE' })));
      toast.success(`${ids.length} prospect(s) supprimes`); load();
    } catch { toast.error('Erreur'); }
  };

  const NAF_CODES = {
    entreprise: { code: '56.10A / 56.21Z', label: 'Restauration & Traiteur' },
    ce: { code: '94.99Z', label: 'Activites des organisations' },
    seminaire: { code: '55.10Z / 82.30Z', label: 'Hotels & Congres' },
    famille: { code: '93.29Z', label: 'Activites recreatives' },
  };

  const TABS = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3, mobile: true },
    { id: 'pipeline', label: 'Pipeline', icon: Target, mobile: true },
    { id: 'rapide', label: 'Rapide', icon: Zap, mobile: true },
    { id: 'relances', label: 'Relances', icon: Clock, mobile: true, badge: relances.length > 0 ? relances.length : null },
    { id: 'programmes', label: 'Programmes', icon: Clock, mobile: false },
    { id: 'recherche', label: 'Recherche', icon: Search, mobile: false },
    { id: 'sources', label: 'Sources', icon: Globe, mobile: false },
    { id: 'config', label: 'Config', icon: Settings, mobile: false },
    { id: 'ia-knowledge', label: 'IA', icon: BookOpen, mobile: true },
    { id: 'ai-assistant', label: 'Assistant', icon: Sparkles, mobile: true },
    { id: 'guide', label: 'Guide', icon: BookOpen, mobile: true },
    { id: 'signatures', label: 'Signatures', icon: PenLine, mobile: false },
    { id: 'emails', label: 'Emails', icon: Mail, mobile: true },
    { id: 'corbeille', label: 'Corbeille', icon: Trash2, mobile: false },
  ];

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 size={24} className="animate-spin text-[#1B2A52]" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24" data-testid="crm-page">
      {showGoProspecter && (
        <GoProspecter prospects={prospects} onUpdate={(p) => setProspects(prev => prev.map(pr => pr.id === p.id ? p : pr))}
          onRefresh={load} catalogue={catalogue} onClose={() => { setShowGoProspecter(false); load(); }} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#1B2A52] tracking-tight">CRM Traiteur</h1>
          <p className="text-xs text-gray-400">{prospects.length} prospect{prospects.length > 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} size="sm" className="bg-[#009893] hover:bg-[#00807b] text-white" data-testid="add-prospect-btn">
          <Plus size={14} className="mr-1" /> Nouveau
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-3 mb-4 border-b border-gray-100 -mx-4 px-4 scrollbar-hide">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
                tab === t.id ? 'bg-[#1B2A52] text-white shadow-sm' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`} data-testid={`tab-${t.id}`}>
              <Icon size={13} />{t.label}
              {t.badge && <span className="ml-1 px-1.5 py-0.5 text-[9px] rounded-full bg-red-500 text-white font-bold">{t.badge}</span>}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {tab === 'dashboard' && <CrmDashboard stats={stats} relances={relances} hotProspects={hotProspects} caSecteurs={caSecteurs}
        objectif={objectif} onSetObjectif={handleSetObjectif} onDismissHot={(id) => setDismissedHot(prev => new Set([...prev, id]))}
        onDeleteProspect={deleteProspect} onGoProspecter={() => setShowGoProspecter(true)} onSetTab={setTab} />}
      {tab === 'pipeline' && <CrmPipeline prospects={prospects} onSelect={setSelectedProspect} onBulkUpdate={bulkUpdate} onBulkDelete={bulkDelete} onRefresh={load} />}
      {tab === 'rapide' && <CrmRapide prospects={prospects} onRefresh={load} />}
      {tab === 'relances' && <CrmRelances onRefresh={load} />}
      {tab === 'programmes' && <CrmProgrammes />}
      {tab === 'recherche' && <CrmRecherche onImport={load} />}
      {tab === 'sources' && <CrmSources nafCodes={NAF_CODES} />}
      {tab === 'config' && <CrmConfig catalogue={catalogue} onSave={setCatalogue} />}
      {tab === 'ia-knowledge' && <CrmIAKnowledge />}
      {tab === 'ai-assistant' && <CrmAIAssistant selectedProspect={selectedProspect} />}
      {tab === 'guide' && <CrmGuide onNavigate={setTab} />}
      {tab === 'signatures' && <CrmSignatures />}
      {tab === 'emails' && <CrmEmailLogs />}
      {tab === 'corbeille' && <CrmCorbeille onRestored={load} />}

      {/* Modals */}
      {selectedProspect && <ProspectDetail prospect={selectedProspect} onClose={() => { setSelectedProspect(null); load(); }}
        onUpdate={(p) => { setProspects(prev => prev.map(pr => pr.id === p.id ? p : pr)); setSelectedProspect(p); }}
        onDelete={(id) => { setProspects(prev => prev.filter(pr => pr.id !== id)); setSelectedProspect(null); }}
        onRefresh={load} catalogue={catalogue} />}
      {showCreate && <CreateProspectModal onClose={() => { setShowCreate(false); load(); }} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}
